import { confirmSignUp, resendSignUpCode, signOut, signUp } from 'aws-amplify/auth';
import { makeAutoObservable, runInAction } from 'mobx';
import { clearClinikoPatientId } from '../components/helper/clinikoParamsHelper';
import { getCurrentSession, signInAndGetUser } from '../react-core-lib/third-party/amplify-auth';
import UserApi from '../rest-api/user-api';
import DownloadUtils from '../utils/download-utils';

export const AuthState = Object.freeze({
    UNINITIALIZED: 'uninitialized',
    UNVERIFIED: 'unverified',
    AUTHENTICATED: 'authenticated',
    UNAUTHENTICATED: 'unauthenticated',
    PENDING_SUBSCRIPTION: 'pending_subscription',
    CANCELLED_SUBSCRIPTION: 'cancelled_subscription',
});

// There are 3 initialisation cases to handle
// 1. User is already authenticated when entering
// 2. User signs in successfully
// 3. User confirms code and signs in successfully
// In all cases, the asyncAppInitializer is called with the Cognito User ID
// The asyncAppInitializer is a function that takes the Cognito User ID as a parameter and returns a promise
// The asyncAppInitializer is called before the AuthStore is updated with the authenticated state
// This way we don't render any components that depend on the authenticated state until the asyncAppInitializer has completed

class AuthStore {
    idToken = '';
    email = '';
    tempPassword = '';
    authState = AuthState.UNINITIALIZED;

    constructor(appStateStore, asyncAppInitializer) {
        makeAutoObservable(this);
        this.appStateStore = appStateStore;
        this.initAuth(asyncAppInitializer);
    }

    setTempPassword(password) {
        this.tempPassword = password;
    }

    async refreshToken() {
        try {
            const session = await getCurrentSession();
            if (session && session.isValid()) {
                const refreshInterval = this.calculateRefreshInterval(session);

                runInAction(() => {
                    this.idToken = session.getIdToken().getJwtToken();
                });

                // Clear any existing interval and set a new one
                if (this.tokenRefreshInterval) clearInterval(this.tokenRefreshInterval);
                this.tokenRefreshInterval = setInterval(() => {
                    this.refreshToken();
                }, refreshInterval);
            } else {
                console.log('Token is not valid or expired. You should handle this case.');
            }
        } catch (error) {
            console.error('An error occurred during token refresh:', error);
        }
    }

    calculateRefreshInterval(session) {
        const expiryTime = session.getIdToken().payload.exp * 1000; // Token expiry time in ms
        const currentTime = Date.now();
        const offsetTime = 5 * 60 * 1000; // Offset time of 5 minutes in ms
        let refreshInterval = expiryTime - currentTime - offsetTime;

        // Ensure that we always have at least a 1-minute buffer, regardless of the token's expiry time
        const minimumBuffer = 1 * 60 * 1000; // 1 minute in ms
        if (refreshInterval < minimumBuffer) {
            refreshInterval = minimumBuffer;
        }

        return refreshInterval > 0 ? refreshInterval : 0;
    }

    startTokenRefreshInterval() {
        this.refreshToken(); // Initial call to refresh the token and set up the interval
    }

    async initAuth(asyncAppInitializer) {
        try {
            const session = await getCurrentSession();
            if (session && session.isValid()) {
                console.warn('session found and isValid');
                const cognitoUserId = session.idToken.payload.sub;

                const idToken = session.getIdToken().getJwtToken();
                await asyncAppInitializer(cognitoUserId, idToken);

                await this.checkAndSetAuthState(session.idToken.payload.email, idToken);

                this.startTokenRefreshInterval();
            } else {
                runInAction(() => {
                    this.authState = AuthState.UNAUTHENTICATED;
                });
            }
        } catch (error) {
            console.error('An error occurred while initializing auth:', error);
            runInAction(() => {
                this.authState = AuthState.UNAUTHENTICATED;
            });
        }
    }

    async hasActiveSubscription() {
        // Retrieve user data from the existing AppStateStore instance and log it
        const userData = await this.appStateStore.getUserDataStoreData();

        const { accountType, trialActive, trialEndDate, subscriptionEndDate } = userData.subscriptionData;

        const currentTime = Date.now();

        if (accountType === 'Clinic-multiuser' && subscriptionEndDate > currentTime) {
            return true;
        }

        // Check if any of the subscription fields are undefined
        if (accountType === undefined || trialActive === undefined) {
            console.error('Subscription data is incomplete or malformed:', userData.subscriptionData);
            return false; // Treat incomplete or malformed data as no active subscription
        }

        // Validate trial data consistency
        // Note: trialEndDate and subscriptionEndDate can be null for Free plan users
        if (trialActive === true) {
            if (trialEndDate === null || trialEndDate === undefined) {
                console.error('Data inconsistency: trialActive is true but trialEndDate is null/undefined', {
                    accountType,
                    trialActive,
                    trialEndDate,
                });
                // Continue processing - will fail the time check below
            } else if (isNaN(trialEndDate)) {
                console.error('Invalid trialEndDate format:', trialEndDate);
                return false;
            }
        }

        // Validate subscription end date for paid plans
        if (accountType && accountType !== 'Free' && subscriptionEndDate !== null) {
            if (isNaN(subscriptionEndDate)) {
                console.error('Invalid subscriptionEndDate format:', subscriptionEndDate);
                return false;
            }
        }

        // Log values for debugging
        console.warn('Current time:', currentTime);
        console.warn('Trial active:', trialActive);
        console.warn('Trial end date:', trialEndDate);
        console.warn('Account type:', accountType);
        console.warn('Subscription end date:', subscriptionEndDate);

        // Determine if the user has an active trial or subscription
        if (trialActive === true && trialEndDate !== null && trialEndDate > currentTime) {
            return true; // User is on trial
        } else if (accountType === 'Free') {
            return true; // Free plan is always active (no expiry)
        } else if (accountType && subscriptionEndDate !== null && subscriptionEndDate > currentTime) {
            return true; // User has an active subscription
        }

        return false; // User has no active trial or subscription
    }

    async ensureUserDataHydratedFromApi(token) {
        try {
            const userData = await this.appStateStore.getUserDataStoreData();
            const hasEmail = !!userData?.emailAddress;
            const hasAccountType = !!userData?.subscriptionData?.accountType;

            if (hasEmail && hasAccountType) {
                return;
            }

            if (!token) {
                console.warn('[AuthStore] Skipping hydration fallback: missing token');
                return;
            }

            console.warn('[AuthStore] Running hydration fallback from /clinicians/query');
            const clinicianDataResponse = await UserApi.getClinicianData(token);

            if (!clinicianDataResponse?.userData?.emailAddress) {
                console.warn('[AuthStore] Hydration fallback: no clinician userData/email in response');
                return;
            }

            const mappedData = {
                firstName: clinicianDataResponse.userData.firstName || '',
                lastName: clinicianDataResponse.userData.lastName || '',
                emailAddress: clinicianDataResponse.userData.emailAddress || '',
                mobile: clinicianDataResponse.userData.mobile || '',
                clinicName: clinicianDataResponse?.clinicData?.clinicName || '',
                clinicNumber: clinicianDataResponse?.clinicData?.clinicNumber || '',
                clinicSize: clinicianDataResponse?.clinicData?.clinicSize || '',
                clinicImageId: '',
                showLogoInHandouts: clinicianDataResponse.showLogoInHandouts,
                handouts: JSON.stringify(clinicianDataResponse.handouts || []),
                recentSearches: clinicianDataResponse.recentSearches || [],
                whiteboards: clinicianDataResponse.whiteboards || [],
                whiteboardsFavourites: clinicianDataResponse.whiteboardsFavourites || [],
                customisedHandouts: clinicianDataResponse.customisedHandouts || [],
                customisedHandoutsFavourites: clinicianDataResponse.customisedHandoutsFavourites || [],
                savedHandouts: clinicianDataResponse.savedHandouts || [],
                savedHandoutsFavourites: clinicianDataResponse.savedHandoutsFavourites || [],
                lastUsedColours: clinicianDataResponse?.lastUsedColours ?? { pencil: [], spray: [] },
                subscriptionData: clinicianDataResponse.subscriptionData || undefined,
            };

            if (clinicianDataResponse?.clinicData?.clinicImageId) {
                try {
                    const payload = {
                        files: [{ method: 'get', key: clinicianDataResponse.clinicData.clinicImageId }],
                        contentCategory: 'clinic_image',
                    };
                    const clinicImageURL = await UserApi.generatePresignedUrls(token, payload);
                    if (clinicImageURL?.getUrls?.[0]?.presignedUrl) {
                        const clinicImageBase64 = await DownloadUtils.downloadImageFromPresignedUrl(
                            clinicImageURL.getUrls[0].presignedUrl,
                        );
                        mappedData.clinicImageId = clinicImageBase64.base64data;
                    }
                } catch (presignError) {
                    console.error('[AuthStore] Hydration fallback failed to load clinic image:', presignError);
                }
            }

            this.appStateStore.userDataStore?.setUserData(mappedData);
            console.warn('[AuthStore] Hydration fallback stored userData in UserDataStore');
        } catch (error) {
            console.error('[AuthStore] Hydration fallback failed:', error);
        }
    }

    async checkAndSetAuthState(email, token) {
        await this.ensureUserDataHydratedFromApi(token);

        const activeSubscription = await this.hasActiveSubscription();
        const userData = await this.appStateStore.getUserDataStoreData();
        const { cancelled, subscriptionEndDate } = userData.subscriptionData;
        const currentTime = Date.now();

        // Check if the subscription cancelled and expired
        if (cancelled && currentTime > subscriptionEndDate) {
            this.setCancelledSubscriptionState(email, token);
        } else if (activeSubscription) {
            this.setAuthenticatedState(email, token);
        } else {
            this.setPendingSubscriptionState(email, token);
        }
    }

    // We want to lower case to prevent user mistakes
    async signUp(email, password) {
        const lowercaseEmail = email.toLowerCase();

        try {
            await signUp({ username: lowercaseEmail, password });
            // console.log('User name is:', data.user.getUsername());
            // console.log('Sign-up result:', JSON.stringify(data));
            runInAction(() => {
                this.email = lowercaseEmail;
                this.tempPassword = password;
                this.authState = AuthState.UNVERIFIED;
            });
            return { success: true };
        } catch (err) {
            console.error(err);
            runInAction(() => {
                this.authState = AuthState.UNAUTHENTICATED;
            });
            throw err;
        }
    }

    setAuthenticatedState(email, token) {
        runInAction(() => {
            this.tempPassword = '';
            this.email = email;
            this.idToken = token;
            this.authState = AuthState.AUTHENTICATED;
        });
    }

    setPendingSubscriptionState(email, token) {
        runInAction(() => {
            this.tempPassword = '';
            this.email = email;
            this.idToken = token;
            this.authState = AuthState.PENDING_SUBSCRIPTION;
        });
    }

    setCancelledSubscriptionState(email, token) {
        runInAction(() => {
            this.tempPassword = '';
            this.email = email;
            this.idToken = token;
            this.authState = AuthState.CANCELLED_SUBSCRIPTION;
        });
    }

    async signInUser(email, password, asyncAppInitializer) {
        let lowercaseEmail;
        let user;
        let token;
        let userId;

        try {
            lowercaseEmail = email.toLowerCase();
            user = await signInAndGetUser(lowercaseEmail, password);

            token = user.signInUserSession.getIdToken().getJwtToken();
            userId = user.attributes.sub;
        } catch (err) {
            console.log('authentication failed', err);
            return { success: false, message: err.message, isAuthError: true };
        }

        try {
            // Wait for the asyncAppInitializer to complete before setting authenticated state
            if (asyncAppInitializer && typeof asyncAppInitializer === 'function') {
                await asyncAppInitializer(userId, token);
            }

            const activeSubscription = await this.hasActiveSubscription();
            const userData = await this.appStateStore.getUserDataStoreData();
            const { cancelled, subscriptionEndDate } = userData.subscriptionData;
            const currentTime = Date.now();

            if (cancelled && currentTime > subscriptionEndDate) {
                this.setCancelledSubscriptionState(lowercaseEmail, token);
            } else if (activeSubscription) {
                this.setAuthenticatedState(lowercaseEmail, token);
            } else {
                this.setPendingSubscriptionState(lowercaseEmail, token);
            }

            return { success: true, token };
        } catch (err) {
            console.error('post-login initialization failed', err);
            return {
                success: false,
                message:
                    err.message ||
                    'Signed in successfully, but we could not finish loading your account. Please refresh and try again.',
                isAuthError: false,
            };
        }
    }

    async doConfirmRegistration(email, code) {
        try {
            const lowercaseEmail = email.toLowerCase();
            await confirmSignUp({ username: lowercaseEmail, confirmationCode: code });
            // console.log('Verification result:', result);
            localStorage.removeItem('userData');
        } catch (err) {
            console.error(err);

            // Check if the error message is about the user already being confirmed
            if (err.message === 'User cannot be confirmed. Current status is CONFIRMED') {
                console.log('User already confirmed, proceeding to authenticate.');
            } else {
                throw err;
            }
        }
    }

    async confirmRegistration(email, password, code, asyncAppInitializer) {
        try {
            if (!email || email.length === 0) {
                console.error('Email is empty');
                throw new Error(
                    'Sorry, we are unable to process your request at this time. Please contact us at hello@cetrahealth.com for further assistance.',
                );
            }
            if (!password || password.length === 0) {
                console.error('Temporary password is empty');
                throw new Error(
                    'Sorry, we are unable to process your request at this time. Please contact us at hello@cetrahealth.com for further assistance.',
                );
            }

            const lowercaseEmail = email.toLowerCase();
            await this.doConfirmRegistration(lowercaseEmail, code);
            return await this.signInUser(lowercaseEmail, password, asyncAppInitializer);
        } catch (err) {
            console.error(err);
            runInAction(() => {
                this.authState = AuthState.UNVERIFIED;
            });
            throw err;
        }
    }

    setIdToken(token) {
        runInAction(() => {
            this.idToken = token;
            this.authState = AuthState.AUTHENTICATED;
        });
    }

    async logout() {
        try {
            clearClinikoPatientId();
            await signOut();
            runInAction(() => {
                this.idToken = '';
                this.authState = AuthState.UNAUTHENTICATED;
            });
        } catch (err) {
            console.error('error signing out:', err);
        }
    }

    async resendVerificationCode(email) {
        try {
            const lowercaseEmail = email.toLowerCase();
            console.log(`Resending verification code to: ${lowercaseEmail}`);
            await resendSignUpCode({ username: lowercaseEmail });
            console.log(`Verification code resent to ${lowercaseEmail}`);
            return { success: true };
        } catch (err) {
            console.error('Error in resendVerificationCode:', err);
            throw err;
        }
    }
    isAuthenticated() {
        return this.authState === AuthState.AUTHENTICATED || this.authState === AuthState.IFRAME_AUTHENTICATED;
    }
}

const createAuthStore = (appStateStore, asyncAppInitializer) => new AuthStore(appStateStore, asyncAppInitializer);
export default createAuthStore;
